import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Upload, Copy, Trash2, ChevronUp, ChevronDown, Loader2, FileText, ImageIcon, Pencil, Check, X, Share2, Send,
} from "lucide-react";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,application/pdf";

export default function PatientResources() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [headline, setHeadline] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/patient-resources");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    if (!headline) setHeadline(f.name.replace(/\.[^.]+$/, ""));
    setUploadOpen(true);
  };

  const onFileInput = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    pickFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    pickFile(e.dataTransfer.files?.[0]);
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("headline", headline || file.name);
      await api.post("/patient-resources", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      setUploadOpen(false); setFile(null); setHeadline("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const startEdit = (r) => { setEditingId(r.id); setEditingText(r.headline); };
  const cancelEdit = () => { setEditingId(null); setEditingText(""); };
  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await api.patch(`/patient-resources/${editingId}`, { headline: editingText });
      toast.success("Headline updated");
      cancelEdit();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const move = async (r, direction) => {
    try {
      await api.post(`/patient-resources/${r.id}/move`, { direction });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyResource = async (r) => {
    const url = `${API}/patient-resources/${r.id}/file`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("Fetch failed");
      const blob = await res.blob();

      if (r.kind === "image") {
        try {
          const pngBlob = await ensurePng(blob);
          await navigator.clipboard.write([
            new ClipboardItem({ "image/png": pngBlob }),
          ]);
          toast.success("Image copied — paste it into WhatsApp");
          return;
        } catch (clipErr) {
          console.warn("Clipboard image copy failed, falling back", clipErr);
          // fall through to share/download
        }
      }

      // PDFs (or image fallback): use Web Share API with files if available
      const file = new File([blob], r.file_name || "file", { type: blob.type });
      if (typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: r.headline });
          return;
        } catch (shareErr) {
          // user cancelled or share failed — fall through to download
          if (shareErr?.name === "AbortError") return;
        }
      }

      // Last resort: trigger a download so the user can attach it manually
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = r.file_name || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("File downloaded — attach it in WhatsApp");
    } catch (err) {
      console.error(err);
      toast.error("Copy failed — try again");
    }
  };

  const sendOnWhatsApp = async (r) => {
    const url = `${API}/patient-resources/${r.id}/file`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("Fetch failed");
      const blob = await res.blob();
      const file = new File([blob], r.file_name || "file", { type: blob.type });

      // Mobile / share-capable browsers: hand the file to the native share sheet
      // (WhatsApp appears with the file already attached).
      if (typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: r.headline, text: r.headline });
          return;
        } catch (shareErr) {
          if (shareErr?.name === "AbortError") return;
        }
      }

      // Desktop: stage the file so the user can paste/attach after picking a contact.
      if (r.kind === "image") {
        try {
          const pngBlob = await ensurePng(blob);
          await navigator.clipboard.write([
            new ClipboardItem({ "image/png": pngBlob }),
          ]);
          toast.success("Image copied — pick a contact in WhatsApp and paste (Ctrl/Cmd+V)");
        } catch (clipErr) {
          console.warn("Clipboard image copy failed", clipErr);
        }
      } else {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = r.file_name || "file";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objUrl);
        toast.success("File downloaded — pick a contact in WhatsApp and attach it");
      }

      // Launch the native WhatsApp desktop app with the headline pre-typed and
      // the contact picker open.
      const waUrl = `whatsapp://send?text=${encodeURIComponent(r.headline || "")}`;
      const link = document.createElement("a");
      link.href = waUrl;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      toast.error("Couldn't send on WhatsApp — try again");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/patient-resources/${deleteTarget.id}`);
      toast.success("Removed");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl" data-testid="patient-resources-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Patient Resources</h1>
          <p className="text-sm text-slate-500 mt-1">
            Store images and PDFs you often share with patients. Tap <span className="font-medium">Copy image</span> to paste it straight into WhatsApp, or <span className="font-medium">Copy file</span> for PDFs.
          </p>
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={onFileInput}
            data-testid="hidden-file-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            className="bg-sky-500 hover:bg-sky-600 text-white"
            data-testid="add-resource-button"
          >
            <Upload size={16} className="mr-2" /> Add resource
          </Button>
        </div>
      </div>

      {/* Drop zone (also acts as empty state) */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="mb-6 border-2 border-dashed border-slate-200 hover:border-sky-300 rounded-xl px-6 py-6 bg-white text-center text-sm text-slate-500 cursor-pointer"
        onClick={() => fileRef.current?.click()}
        data-testid="dropzone"
      >
        <Upload size={18} className="inline mr-2 text-slate-400" />
        Drop an image or PDF here, or <span className="text-sky-600 font-medium">click to browse</span>
        <span className="block text-xs text-slate-400 mt-1">Up to 15 MB. JPG, PNG, WEBP, GIF or PDF.</span>
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-10">
          <Loader2 className="animate-spin inline mr-2" size={16} /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center text-slate-500 py-10 text-sm" data-testid="empty-state">
          No resources yet. Add your first one above.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((r, idx) => (
            <Card key={r.id} className="p-4 border-slate-200" data-testid={`resource-${r.id}`}>
              <div className="flex items-start gap-4">
                {/* Reorder */}
                <div className="flex flex-col gap-1 pt-1">
                  <Button
                    size="icon" variant="ghost"
                    disabled={idx === 0}
                    onClick={() => move(r, "up")}
                    aria-label="Move up"
                    className="h-7 w-7"
                    data-testid={`move-up-${r.id}`}
                  >
                    <ChevronUp size={16} />
                  </Button>
                  <Button
                    size="icon" variant="ghost"
                    disabled={idx === items.length - 1}
                    onClick={() => move(r, "down")}
                    aria-label="Move down"
                    className="h-7 w-7"
                    data-testid={`move-down-${r.id}`}
                  >
                    <ChevronDown size={16} />
                  </Button>
                </div>

                {/* Preview */}
                <a
                  href={`${API}/patient-resources/${r.id}/file`}
                  target="_blank" rel="noreferrer"
                  className="shrink-0 h-24 w-24 rounded-md border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center"
                  data-testid={`preview-${r.id}`}
                >
                  {r.kind === "image" ? (
                    <img src={`${API}/patient-resources/${r.id}/file`} alt={r.headline} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-slate-500">
                      <FileText size={28} />
                      <span className="text-[10px] font-medium">PDF</span>
                    </div>
                  )}
                </a>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {editingId === r.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        autoFocus
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit();
                          if (e.key === "Escape") cancelEdit();
                        }}
                        className="h-9"
                        data-testid={`edit-headline-input-${r.id}`}
                      />
                      <Button size="icon" variant="outline" onClick={saveEdit}
                              className="h-9 w-9" aria-label="Save"
                              data-testid={`save-headline-${r.id}`}>
                        <Check size={16} />
                      </Button>
                      <Button size="icon" variant="outline" onClick={cancelEdit}
                              className="h-9 w-9" aria-label="Cancel">
                        <X size={16} />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 group">
                      <h3 className="text-base font-semibold text-slate-900 truncate"
                          data-testid={`headline-${r.id}`}>
                        {r.headline || <span className="text-slate-400 italic">Untitled</span>}
                      </h3>
                      <button
                        onClick={() => startEdit(r)}
                        className="text-slate-400 hover:text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Edit headline"
                        data-testid={`edit-headline-${r.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                  )}
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                    {r.kind === "image" ? <ImageIcon size={12} /> : <FileText size={12} />}
                    <span className="truncate">{r.file_name}</span>
                    <span>·</span>
                    <span>{formatBytes(r.size)}</span>
                  </div>

                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      onClick={() => copyResource(r)}
                      className="bg-sky-500 hover:bg-sky-600 text-white"
                      data-testid={`copy-file-${r.id}`}
                    >
                      {r.kind === "image" ? (
                        <><Copy size={14} className="mr-1.5" /> Copy image</>
                      ) : (
                        <><Share2 size={14} className="mr-1.5" /> Copy file</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => sendOnWhatsApp(r)}
                      className="bg-[#25D366] hover:bg-[#1EBE5A] text-white"
                      data-testid={`whatsapp-${r.id}`}
                      title="Open WhatsApp with this file attached"
                    >
                      <Send size={14} className="mr-1.5" /> WhatsApp
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setDeleteTarget(r)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      aria-label={`Delete ${r.headline}`}
                      data-testid={`delete-${r.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Upload dialog (headline + confirm) */}
      <Dialog open={uploadOpen} onOpenChange={(v) => { if (!v && !uploading) { setUploadOpen(false); setFile(null); setHeadline(""); } }}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Add a patient resource</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitUpload} className="space-y-4">
            {file && (
              <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                {file.type.startsWith("image/") ? (
                  <ImageIcon size={20} className="text-slate-500" />
                ) : (
                  <FileText size={20} className="text-slate-500" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">{file.name}</div>
                  <div className="text-xs text-slate-500">{formatBytes(file.size)}</div>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Headline</label>
              <Input
                required
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="e.g. Post-extraction care instructions"
                data-testid="upload-headline-input"
              />
              <p className="text-xs text-slate-500">Shown above the file. You can rename it anytime.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={uploading}
                      onClick={() => { setUploadOpen(false); setFile(null); setHeadline(""); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading || !file}
                      className="bg-sky-500 hover:bg-sky-600 text-white"
                      data-testid="submit-upload">
                {uploading ? <Loader2 className="animate-spin" size={16} /> : "Upload"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-resource-dialog">
          <DialogHeader>
            <DialogTitle>Remove this resource?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            <span className="font-medium">{deleteTarget?.headline}</span> will be removed from your list.
            Any link you already shared with a patient will stop working.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-resource"
            >
              {deleting ? <Loader2 className="animate-spin" size={16} /> : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatBytes(b) {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// Convert any image blob to a PNG blob via canvas so the Clipboard API accepts it.
function ensurePng(blob) {
  return new Promise((resolve, reject) => {
    if (blob.type === "image/png") return resolve(blob);
    const img = new Image();
    const objUrl = URL.createObjectURL(blob);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((b) => {
        URL.revokeObjectURL(objUrl);
        if (b) resolve(b); else reject(new Error("toBlob failed"));
      }, "image/png");
    };
    img.onerror = () => {
      URL.revokeObjectURL(objUrl);
      reject(new Error("Image decode failed"));
    };
    img.src = objUrl;
  });
}
